<template>
  <section class="plan-branding-section">
    <a-divider v-if="showTitle">
      Брендинг планировки
    </a-divider>

    <a-skeleton
      v-if="isLoading"
      active />

    <div v-else>
      <a-alert
        v-if="!hasBranding"
        type="info"
        show-icon
        message="Брендинг для планировки не задан" />

      <div class="branding-panel">
        <div
          v-if="hasBranding"
          class="branding-current">
          <div class="branding-current-header">
            <a-tag color="blue">
              Плановый брендинг
            </a-tag>
            <span class="branding-name">
              {{ currentBranding?.name || currentBranding?.company_name || `#${currentBranding?.id}` }}
            </span>
          </div>
          <div class="branding-meta">
            <span v-if="currentBranding?.company_name">
              Компания: {{ currentBranding.company_name }}
            </span>
            <span v-if="currentBranding?.company_url">
              • {{ currentBranding.company_url }}
            </span>
          </div>
        </div>

        <a-row
          v-if="!hasBranding"
          :gutter="[12, 12]"
          class="branding-attach-row">
          <a-col
            :span="24"
            :md="12">
            <a-select
              v-model:value="selectedBrandingId"
              :options="brandingOptions"
              :style="{width: '100%'}"
              placeholder="Выберите брендинг для привязки"
              allow-clear />
          </a-col>
          <a-col
            :span="24"
            :md="12">
            <a-button
              type="primary"
              :disabled="!selectedBrandingId || !canAttach"
              class="action-btn"
              @click="attachBranding">
              Привязать
            </a-button>
          </a-col>
        </a-row>

        <a-row
          :gutter="[12, 12]"
          class="branding-actions-row">
          <a-col
            v-if="hasBranding"
            :span="24"
            :md="8">
            <a-button
              danger
              :disabled="!canDetach"
              class="action-btn"
              @click="detachBranding">
              Отвязать
            </a-button>
          </a-col>
          <a-col
            v-if="hasBranding"
            :span="24"
            :md="8">
            <router-link :to="brandingEditPath">
              <a-button
                class="action-btn">
                Редактировать
              </a-button>
            </router-link>
          </a-col>
          <a-col
            :span="24"
            :md="hasBranding ? 8 : 24">
            <a-button
              type="primary"
              :disabled="!canCreate"
              class="action-btn"
              @click="openCreateModal">
              Создать брендинг
            </a-button>
          </a-col>
        </a-row>
      </div>
    </div>

    <a-modal
      v-model:open="createModal.isOpened"
      centered
      title="Создать брендинг для планировки"
      ok-text="Создать"
      cancel-text="Отменить"
      :confirm-loading="createModal.isCreating"
      :ok-button-props="{ disabled: !isCreateBrandingFormValid || !canCreate }"
      @ok="createBranding">
      <a-form
        :model="createModal.form"
        layout="vertical">
        <a-form-item
          label="Название"
          required>
          <a-input
            v-model:value="createModal.form.name"
            placeholder="Название брендинга" />
        </a-form-item>
        <a-form-item
          label="Компания"
          required>
          <a-input
            v-model:value="createModal.form.company_name"
            placeholder="Название компании" />
        </a-form-item>
        <a-form-item label="Сайт компании">
          <a-input
            v-model:value="createModal.form.company_url"
            placeholder="https://example.com" />
        </a-form-item>
        <a-form-item
          label="Язык виджета"
          required>
          <a-select
            v-model:value="createModal.form.widget_language"
            :options="languageOptions" />
        </a-form-item>
        <a-form-item label="CDN">
          <a-select
            v-model:value="createModal.form.cdn_type"
            :options="cdnOptions"
            placeholder="По умолчанию: RU"
            allow-clear />
        </a-form-item>
        <a-form-item label="Описание">
          <a-textarea
            v-model:value="createModal.form.description"
            :auto-size="{ minRows: 2, maxRows: 4 }" />
        </a-form-item>
        <a-form-item label="Логотип (png, svg)">
          <a-upload
            :accept="'.svg, .png'"
            :before-upload="handleLogoUpload">
            <a-button>Загрузить</a-button>
          </a-upload>
        </a-form-item>
      </a-form>
    </a-modal>
  </section>
</template>

<script setup>
import {
  computed, reactive, ref, watch,
} from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { message, Modal } from 'ant-design-vue';
import { api } from '@/api';

const props = defineProps({
  planId: {
    type: Number,
    required: true,
  },
  canEdit: {
    type: Boolean,
    default: true,
  },
  showTitle: {
    type: Boolean,
    default: true,
  },
});

const router = useRouter();
const store = useStore();
const canAttach = computed(() => props.canEdit
  && store.getters['auth/hasPermission']('api.crm.plans.branding.attach'));
const canDetach = computed(() => props.canEdit
  && store.getters['auth/hasPermission']('api.crm.plans.branding.detach'));
const canCreate = computed(() => props.canEdit
  && store.getters['auth/hasPermission']('api.crm.plans.branding.create'));

const isLoading = ref(false);
const hasBranding = ref(false);
const currentBranding = ref(null);
const availableBrandings = ref([]);
const selectedBrandingId = ref(null);

const languageOptions = [
  { value: 'ru', label: 'ru' },
  { value: 'en', label: 'en' },
  { value: 'de', label: 'de' },
  { value: 'es', label: 'es' },
];
const cdnOptions = [
  { value: 'ru', label: 'RU' },
  { value: 'int', label: 'INT' },
];

const brandingOptions = computed(() => availableBrandings.value.map((branding) => ({
  value: branding.id,
  label: branding.name || branding.company_name || `#${branding.id}`,
})));

const brandingEditPath = computed(() => {
  if (!currentBranding.value?.id) return '';
  const r = router.resolve({ name: 'branding-show', params: { id: currentBranding.value.id } });
  return r.path;
});

const loadData = async () => {
  if (!props.planId) return;
  isLoading.value = true;

  try {
    const [brandingResp, availableResp] = await Promise.all([
      api.getPlanBranding(props.planId),
      api.getAvailablePlanBrandings(),
    ]);

    const brandingData = brandingResp?.data;
    hasBranding.value = Boolean(brandingData?.has_branding);
    currentBranding.value = brandingData?.branding || null;

    availableBrandings.value = availableResp?.data?.brandings || [];
    if (currentBranding.value?.id) {
      selectedBrandingId.value = currentBranding.value.id;
    }
  } catch (error) {
    message.error(error.response?.data?.message || 'Не удалось загрузить брендинг');
  } finally {
    isLoading.value = false;
  }
};

const attachBranding = async () => {
  if (!selectedBrandingId.value) return;
  try {
    await api.attachPlanBranding(props.planId, selectedBrandingId.value);
    message.success('Брендинг привязан');
    await loadData();
  } catch (error) {
    message.error(error.response?.data?.message || 'Не удалось привязать брендинг');
  }
};

const detachBranding = () => Modal.confirm({
  title: 'Отвязать брендинг от планировки?',
  okType: 'danger',
  okText: 'Отвязать',
  cancelText: 'Отмена',
  onOk: async () => {
    try {
      await api.detachPlanBranding(props.planId, true);
      message.success('Брендинг отвязан');
      await loadData();
    } catch (error) {
      message.error(error.response?.data?.message || 'Не удалось отвязать брендинг');
    }
  },
});

const createModal = reactive({
  isOpened: false,
  isCreating: false,
  form: {
    name: '',
    company_name: '',
    company_url: '',
    widget_language: 'en',
    cdn_type: null,
    description: '',
    company_logo: null,
  },
});

const isCreateBrandingFormValid = computed(() => (
  Boolean(createModal.form.name?.trim())
  && Boolean(createModal.form.company_name?.trim())
  && Boolean(createModal.form.widget_language?.trim())
));

const openCreateModal = () => {
  if (!canCreate.value) return;
  createModal.isOpened = true;
};

const closeCreateModal = () => {
  createModal.form = {
    name: '',
    company_name: '',
    company_url: '',
    widget_language: 'en',
    cdn_type: null,
    description: '',
    company_logo: null,
  };
  createModal.isOpened = false;
};

const handleLogoUpload = (file) => {
  const isSvgOrPng = file.type === 'image/svg+xml' || file.type === 'image/png';
  if (!isSvgOrPng) {
    message.error('Допустимые типы файлов: SVG, PNG');
    return false;
  }
  createModal.form.company_logo = file;
  return false;
};

const createBranding = async () => {
  if (!isCreateBrandingFormValid.value) {
    message.error('Заполните название, компанию и язык');
    return;
  }

  createModal.isCreating = true;

  try {
    const formData = new FormData();
    formData.append('name', createModal.form.name);
    formData.append('company_name', createModal.form.company_name);
    formData.append('widget_language', createModal.form.widget_language);

    if (createModal.form.company_url) {
      formData.append('company_url', createModal.form.company_url);
    }

    if (createModal.form.description) {
      formData.append('description', createModal.form.description);
    }
    if (createModal.form.cdn_type) {
      formData.append('cdn_type', createModal.form.cdn_type);
    }

    if (createModal.form.company_logo) {
      formData.append('company_logo', createModal.form.company_logo);
    }

    await api.createPlanBranding(props.planId, formData);
    message.success('Брендинг создан и привязан');
    closeCreateModal();
    await loadData();
  } catch (error) {
    const validation = error.response?.data?.errors;
    if (error.response?.status === 422 && validation && typeof validation === 'object') {
      Object.values(validation).flat().forEach((msg) => message.error(msg));
      return;
    }
    message.error(error.response?.data?.message || 'Не удалось создать брендинг');
  } finally {
    createModal.isCreating = false;
  }
};

watch(() => props.planId, () => loadData(), { immediate: true });
</script>

<style scoped>
.plan-branding-section {
  margin: 1rem 0;
}

.branding-panel {
  margin-top: 1rem;
  padding: 16px;
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  background: #fafafa;
}

.branding-current {
  padding: 12px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  background: #fff;
}

.branding-current-header {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-wrap: wrap;
}

.branding-attach-row {
  margin-top: 12px;
}

.branding-actions-row {
  margin-top: 12px;
}

.action-btn {
  width: 100%;
}

.action-btn :deep(span) {
  white-space: nowrap;
}

.branding-name {
  font-weight: 600;
}

.branding-meta {
  color: rgba(0, 0, 0, 0.55);
  margin-top: 0.25rem;
}
</style>
